import { GroupStandingsTieBreakerEnum } from '../types/enums';

export type GroupStandingsTieBreakerSource =
  | 'class'
  | 'tournament'
  | 'tour'
  | 'organization'
  | 'default';

export type TeamMatchupScoring = {
  win: number;
  draw: number;
  loss: number;
};

export const DEFAULT_TEAM_MATCHUP_SCORING: TeamMatchupScoring = {
  win: 3,
  draw: 1,
  loss: 0,
};

export type GroupStandingsTieBreakerResolution = {
  effective: GroupStandingsTieBreakerEnum[];
  source: GroupStandingsTieBreakerSource;
  chain: {
    class: GroupStandingsTieBreakerEnum[] | null;
    tournament: GroupStandingsTieBreakerEnum[] | null;
    tour: GroupStandingsTieBreakerEnum[] | null;
    organization: GroupStandingsTieBreakerEnum[] | null;
    default: GroupStandingsTieBreakerEnum[];
  };
  teamMatchupScoring?: TeamMatchupScoring;
  teamMatchupScoringSource?: GroupStandingsTieBreakerSource;
  teamMatchupScoringChain?: {
    class: TeamMatchupScoring | null;
    tournament: TeamMatchupScoring | null;
    tour: TeamMatchupScoring | null;
    organization: TeamMatchupScoring | null;
    default: TeamMatchupScoring;
  };
};

/** Shared RAQT default when the inherit chain falls through (legacy / v1 classes). */
export const DEFAULT_GROUP_STANDINGS_TIE_BREAKERS: GroupStandingsTieBreakerEnum[] = [
  GroupStandingsTieBreakerEnum.TEAM_WINS,
  GroupStandingsTieBreakerEnum.MATCH_WINS,
  GroupStandingsTieBreakerEnum.POINTS_DIFFERENCE,
  GroupStandingsTieBreakerEnum.POINTS_FOR,
  GroupStandingsTieBreakerEnum.POINTS_AGAINST,
  GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_TEAM_WINS,
  GroupStandingsTieBreakerEnum.HEAD_TO_HEAD,
  GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_POINTS,
  GroupStandingsTieBreakerEnum.GAME_DIFFERENCE,
  GroupStandingsTieBreakerEnum.POINTS_PERCENTAGE,
  GroupStandingsTieBreakerEnum.SEEDING,
  GroupStandingsTieBreakerEnum.COIN_TOSS,
];

/** Default for new TEAMS classes (rules version 2). */
export const DEFAULT_TEAM_GROUP_STANDINGS_TIE_BREAKERS: GroupStandingsTieBreakerEnum[] = [
  GroupStandingsTieBreakerEnum.TEAM_POINTS,
  GroupStandingsTieBreakerEnum.TEAM_WINS,
  GroupStandingsTieBreakerEnum.TEAM_DRAWS,
  GroupStandingsTieBreakerEnum.MATCH_WINS,
  GroupStandingsTieBreakerEnum.POINTS_DIFFERENCE,
  GroupStandingsTieBreakerEnum.POINTS_FOR,
  GroupStandingsTieBreakerEnum.POINTS_AGAINST,
  GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_TEAM_WINS,
  GroupStandingsTieBreakerEnum.HEAD_TO_HEAD,
  GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_POINTS,
  GroupStandingsTieBreakerEnum.GAME_DIFFERENCE,
  GroupStandingsTieBreakerEnum.POINTS_PERCENTAGE,
  GroupStandingsTieBreakerEnum.SEEDING,
  GroupStandingsTieBreakerEnum.COIN_TOSS,
];

export const TEAM_ONLY_GROUP_STANDINGS_TIE_BREAKERS = new Set<GroupStandingsTieBreakerEnum>([
  GroupStandingsTieBreakerEnum.TEAM_POINTS,
  GroupStandingsTieBreakerEnum.TEAM_WINS,
  GroupStandingsTieBreakerEnum.TEAM_DRAWS,
  GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_TEAM_WINS,
]);

export const REQUIRED_GROUP_STANDINGS_TIE_BREAKERS: GroupStandingsTieBreakerEnum[] = [
  GroupStandingsTieBreakerEnum.MATCH_WINS,
  GroupStandingsTieBreakerEnum.COIN_TOSS,
];

export const REQUIRED_TEAM_GROUP_STANDINGS_TIE_BREAKERS: GroupStandingsTieBreakerEnum[] = [
  GroupStandingsTieBreakerEnum.TEAM_WINS,
  GroupStandingsTieBreakerEnum.MATCH_WINS,
  GroupStandingsTieBreakerEnum.COIN_TOSS,
];

export const REQUIRED_V2_TEAM_GROUP_STANDINGS_TIE_BREAKERS: GroupStandingsTieBreakerEnum[] = [
  GroupStandingsTieBreakerEnum.TEAM_POINTS,
  GroupStandingsTieBreakerEnum.TEAM_WINS,
  GroupStandingsTieBreakerEnum.TEAM_DRAWS,
  GroupStandingsTieBreakerEnum.MATCH_WINS,
  GroupStandingsTieBreakerEnum.COIN_TOSS,
];

export const ALL_GROUP_STANDINGS_TIE_BREAKERS: GroupStandingsTieBreakerEnum[] = [
  GroupStandingsTieBreakerEnum.TEAM_POINTS,
  GroupStandingsTieBreakerEnum.TEAM_WINS,
  GroupStandingsTieBreakerEnum.TEAM_DRAWS,
  GroupStandingsTieBreakerEnum.MATCH_WINS,
  GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_TEAM_WINS,
  GroupStandingsTieBreakerEnum.HEAD_TO_HEAD,
  GroupStandingsTieBreakerEnum.POINTS_DIFFERENCE,
  GroupStandingsTieBreakerEnum.POINTS_FOR,
  GroupStandingsTieBreakerEnum.POINTS_AGAINST,
  GroupStandingsTieBreakerEnum.GAME_DIFFERENCE,
  GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_POINTS,
  GroupStandingsTieBreakerEnum.POINTS_PERCENTAGE,
  GroupStandingsTieBreakerEnum.SEEDING,
  GroupStandingsTieBreakerEnum.COIN_TOSS,
];

/** Always exposes the full shared list (team criteria included everywhere). */
export const getSelectableGroupStandingsTieBreakers = (): GroupStandingsTieBreakerEnum[] => [
  ...ALL_GROUP_STANDINGS_TIE_BREAKERS,
];

export const isTeamOnlyGroupStandingsTieBreaker = (item: GroupStandingsTieBreakerEnum): boolean =>
  TEAM_ONLY_GROUP_STANDINGS_TIE_BREAKERS.has(item);

export const listUsesV2TeamStandingsCriteria = (
  list: readonly GroupStandingsTieBreakerEnum[]
): boolean =>
  list.includes(GroupStandingsTieBreakerEnum.TEAM_POINTS) ||
  list.includes(GroupStandingsTieBreakerEnum.TEAM_DRAWS);

export const listIncludesTeamPoints = (
  list?: readonly GroupStandingsTieBreakerEnum[] | null
): boolean => Boolean(list?.includes(GroupStandingsTieBreakerEnum.TEAM_POINTS));

export type GroupStandingsTieBreakerRequirementOptions = {
  isTeamsClass?: boolean;
};

export const requiredGroupStandingsTieBreakersForList = (
  list: readonly GroupStandingsTieBreakerEnum[],
  options?: GroupStandingsTieBreakerRequirementOptions
): readonly GroupStandingsTieBreakerEnum[] => {
  if (listUsesV2TeamStandingsCriteria(list)) {
    return REQUIRED_V2_TEAM_GROUP_STANDINGS_TIE_BREAKERS;
  }
  if (options?.isTeamsClass === true) {
    return REQUIRED_TEAM_GROUP_STANDINGS_TIE_BREAKERS;
  }
  return REQUIRED_GROUP_STANDINGS_TIE_BREAKERS;
};

export const hasRequiredGroupStandingsTieBreakers = (
  list: readonly GroupStandingsTieBreakerEnum[],
  options?: GroupStandingsTieBreakerRequirementOptions
): boolean => {
  if (!list.length) {
    return false;
  }
  return requiredGroupStandingsTieBreakersForList(list, options).every((required) =>
    list.includes(required)
  );
};

export const requiredGroupStandingsTieBreakersErrorKey = (
  list: readonly GroupStandingsTieBreakerEnum[],
  options?: GroupStandingsTieBreakerRequirementOptions
): string => {
  if (listUsesV2TeamStandingsCriteria(list)) {
    return 'groupStandingsTieBreaker-requiredCoreErrorV2';
  }
  if (options?.isTeamsClass === true) {
    return 'groupStandingsTieBreaker-requiredCoreErrorTeam';
  }
  return 'groupStandingsTieBreaker-requiredCoreError';
};

export const insertMissingV2TeamCriteria = (
  list: readonly GroupStandingsTieBreakerEnum[]
): GroupStandingsTieBreakerEnum[] => {
  const next = [...list];
  const ensure = (item: GroupStandingsTieBreakerEnum, index: number) => {
    if (!next.includes(item)) {
      next.splice(Math.min(Math.max(index, 0), next.length), 0, item);
    }
  };
  ensure(GroupStandingsTieBreakerEnum.TEAM_POINTS, 0);
  const winsIndex = next.indexOf(GroupStandingsTieBreakerEnum.TEAM_WINS);
  ensure(GroupStandingsTieBreakerEnum.TEAM_WINS, winsIndex >= 0 ? winsIndex : 1);
  ensure(
    GroupStandingsTieBreakerEnum.TEAM_DRAWS,
    next.indexOf(GroupStandingsTieBreakerEnum.TEAM_WINS) + 1
  );
  const matchIndex =
    next.indexOf(GroupStandingsTieBreakerEnum.TEAM_DRAWS) >= 0
      ? next.indexOf(GroupStandingsTieBreakerEnum.TEAM_DRAWS) + 1
      : next.indexOf(GroupStandingsTieBreakerEnum.TEAM_WINS) + 1;
  ensure(GroupStandingsTieBreakerEnum.MATCH_WINS, matchIndex);
  if (!next.includes(GroupStandingsTieBreakerEnum.COIN_TOSS)) {
    next.push(GroupStandingsTieBreakerEnum.COIN_TOSS);
  }
  return next;
};

export const defaultGroupStandingsTieBreakersForClass = (options: {
  isTeamsClass?: boolean;
  rulesVersion?: number | null;
}): GroupStandingsTieBreakerEnum[] => {
  // Existing TEAMS classes stay on the v1 order until they opt in (rules version 2).
  if (options.isTeamsClass === true && (options.rulesVersion ?? 1) < 2) {
    return [...DEFAULT_GROUP_STANDINGS_TIE_BREAKERS];
  }
  // Singles/doubles ranking still starts from the v1 list; team-only rows are stripped later.
  if (options.isTeamsClass === false) {
    return [...DEFAULT_GROUP_STANDINGS_TIE_BREAKERS];
  }
  // Parent-scope blank state (org/tour/tournament) and new TEAMS classes (v2).
  return [...DEFAULT_TEAM_GROUP_STANDINGS_TIE_BREAKERS];
};

export const normalizeTeamMatchupScoring = (
  value?: TeamMatchupScoring | null
): TeamMatchupScoring | null => {
  if (value == null) {
    return null;
  }
  const { win, draw, loss } = value;
  const isValid = (n: unknown) =>
    typeof n === 'number' && Number.isInteger(n) && Number.isSafeInteger(n) && n >= 0;
  if (!isValid(win) || !isValid(draw) || !isValid(loss)) {
    return null;
  }
  return { win, draw, loss };
};

/** Null when inheriting; otherwise the explicit list (empty becomes null). */
export const toPersistedGroupStandingsTieBreakers = (
  inherit: boolean,
  list?: GroupStandingsTieBreakerEnum[] | null
): GroupStandingsTieBreakerEnum[] | null => (inherit ? null : list?.length ? [...list] : null);

export const toPersistedTeamMatchupScoring = (
  inherit: boolean,
  scoring?: TeamMatchupScoring | null
): TeamMatchupScoring | null => (inherit ? null : normalizeTeamMatchupScoring(scoring));

/** Index of the first Coin toss, or -1 if absent. */
export const getCoinTossIndex = (list: readonly GroupStandingsTieBreakerEnum[]): number =>
  list.indexOf(GroupStandingsTieBreakerEnum.COIN_TOSS);

/** True when this row sits after Coin toss (won't run until dragged above). */
export const isCriterionAfterCoinToss = (
  list: readonly GroupStandingsTieBreakerEnum[],
  index: number
): boolean => {
  const coinTossIndex = getCoinTossIndex(list);
  return coinTossIndex >= 0 && index > coinTossIndex;
};

export const stripTeamOnlyGroupStandingsTieBreakers = (
  list: readonly GroupStandingsTieBreakerEnum[]
): GroupStandingsTieBreakerEnum[] =>
  list.filter((item) => !TEAM_ONLY_GROUP_STANDINGS_TIE_BREAKERS.has(item));

export const GROUP_STANDINGS_TIE_BREAKER_LABEL_KEYS: Record<GroupStandingsTieBreakerEnum, string> =
  {
    [GroupStandingsTieBreakerEnum.TEAM_POINTS]: 'groupStandingsTieBreaker-teamPoints',
    [GroupStandingsTieBreakerEnum.TEAM_WINS]: 'groupStandingsTieBreaker-teamWins',
    [GroupStandingsTieBreakerEnum.TEAM_DRAWS]: 'groupStandingsTieBreaker-teamDraws',
    [GroupStandingsTieBreakerEnum.MATCH_WINS]: 'groupStandingsTieBreaker-matchWins',
    [GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_TEAM_WINS]:
      'groupStandingsTieBreaker-headToHeadTeamWins',
    [GroupStandingsTieBreakerEnum.HEAD_TO_HEAD]: 'groupStandingsTieBreaker-headToHead',
    [GroupStandingsTieBreakerEnum.POINTS_DIFFERENCE]: 'groupStandingsTieBreaker-pointsDifference',
    [GroupStandingsTieBreakerEnum.POINTS_FOR]: 'groupStandingsTieBreaker-pointsFor',
    [GroupStandingsTieBreakerEnum.POINTS_AGAINST]: 'groupStandingsTieBreaker-pointsAgainst',
    [GroupStandingsTieBreakerEnum.GAME_DIFFERENCE]: 'groupStandingsTieBreaker-gameDifference',
    [GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_POINTS]: 'groupStandingsTieBreaker-headToHeadPoints',
    [GroupStandingsTieBreakerEnum.POINTS_PERCENTAGE]: 'groupStandingsTieBreaker-pointsPercentage',
    [GroupStandingsTieBreakerEnum.SEEDING]: 'groupStandingsTieBreaker-seeding',
    [GroupStandingsTieBreakerEnum.COIN_TOSS]: 'groupStandingsTieBreaker-coinToss',
  };

export const GROUP_STANDINGS_TIE_BREAKER_DESCRIPTION_KEYS: Record<
  GroupStandingsTieBreakerEnum,
  string
> = {
  [GroupStandingsTieBreakerEnum.TEAM_POINTS]: 'groupStandingsTieBreaker-teamPointsDescription',
  [GroupStandingsTieBreakerEnum.TEAM_WINS]: 'groupStandingsTieBreaker-teamWinsDescription',
  [GroupStandingsTieBreakerEnum.TEAM_DRAWS]: 'groupStandingsTieBreaker-teamDrawsDescription',
  [GroupStandingsTieBreakerEnum.MATCH_WINS]: 'groupStandingsTieBreaker-matchWinsDescription',
  [GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_TEAM_WINS]:
    'groupStandingsTieBreaker-headToHeadTeamWinsDescription',
  [GroupStandingsTieBreakerEnum.HEAD_TO_HEAD]: 'groupStandingsTieBreaker-headToHeadDescription',
  [GroupStandingsTieBreakerEnum.POINTS_DIFFERENCE]:
    'groupStandingsTieBreaker-pointsDifferenceDescription',
  [GroupStandingsTieBreakerEnum.POINTS_FOR]: 'groupStandingsTieBreaker-pointsForDescription',
  [GroupStandingsTieBreakerEnum.POINTS_AGAINST]:
    'groupStandingsTieBreaker-pointsAgainstDescription',
  [GroupStandingsTieBreakerEnum.GAME_DIFFERENCE]:
    'groupStandingsTieBreaker-gameDifferenceDescription',
  [GroupStandingsTieBreakerEnum.HEAD_TO_HEAD_POINTS]:
    'groupStandingsTieBreaker-headToHeadPointsDescription',
  [GroupStandingsTieBreakerEnum.POINTS_PERCENTAGE]:
    'groupStandingsTieBreaker-pointsPercentageDescription',
  [GroupStandingsTieBreakerEnum.SEEDING]: 'groupStandingsTieBreaker-seedingDescription',
  [GroupStandingsTieBreakerEnum.COIN_TOSS]: 'groupStandingsTieBreaker-coinTossDescription',
};

export const getGroupStandingsInheritanceLabelKey = (hasTourSelected: boolean): string =>
  hasTourSelected
    ? 'groupStandingsTieBreaker-useInheritedFromTour'
    : 'groupStandingsTieBreaker-useInheritedFromOrganization';

export const getTeamMatchupScoringInheritanceLabelKey = (hasTourSelected: boolean): string =>
  hasTourSelected
    ? 'teamMatchupScoring-inheritFromTour'
    : 'teamMatchupScoring-inheritFromOrganization';

const resolveScoringFromChain = (scope: {
  classTeamMatchupScoring?: TeamMatchupScoring | null;
  tournamentTeamMatchupScoring?: TeamMatchupScoring | null;
  tourTeamMatchupScoring?: TeamMatchupScoring | null;
  organizationTeamMatchupScoring?: TeamMatchupScoring | null;
}): Pick<
  GroupStandingsTieBreakerResolution,
  'teamMatchupScoring' | 'teamMatchupScoringSource' | 'teamMatchupScoringChain'
> => {
  const scoringClass = normalizeTeamMatchupScoring(scope.classTeamMatchupScoring);
  const scoringTournament = normalizeTeamMatchupScoring(scope.tournamentTeamMatchupScoring);
  const scoringTour = normalizeTeamMatchupScoring(scope.tourTeamMatchupScoring);
  const scoringOrganization = normalizeTeamMatchupScoring(scope.organizationTeamMatchupScoring);
  const scoringDefault: TeamMatchupScoring = { ...DEFAULT_TEAM_MATCHUP_SCORING };
  const teamMatchupScoringChain = {
    class: scoringClass,
    tournament: scoringTournament,
    tour: scoringTour,
    organization: scoringOrganization,
    default: scoringDefault,
  };
  if (scoringClass) {
    return {
      teamMatchupScoring: scoringClass,
      teamMatchupScoringSource: 'class',
      teamMatchupScoringChain,
    };
  }
  if (scoringTournament) {
    return {
      teamMatchupScoring: scoringTournament,
      teamMatchupScoringSource: 'tournament',
      teamMatchupScoringChain,
    };
  }
  if (scoringTour) {
    return {
      teamMatchupScoring: scoringTour,
      teamMatchupScoringSource: 'tour',
      teamMatchupScoringChain,
    };
  }
  if (scoringOrganization) {
    return {
      teamMatchupScoring: scoringOrganization,
      teamMatchupScoringSource: 'organization',
      teamMatchupScoringChain,
    };
  }
  return {
    teamMatchupScoring: scoringDefault,
    teamMatchupScoringSource: 'default',
    teamMatchupScoringChain,
  };
};

export const resolveGroupStandingsTieBreakerResolution = (scope: {
  classTieBreakers?: GroupStandingsTieBreakerEnum[] | null;
  tournamentTieBreakers?: GroupStandingsTieBreakerEnum[] | null;
  tourTieBreakers?: GroupStandingsTieBreakerEnum[] | null;
  organizationTieBreakers?: GroupStandingsTieBreakerEnum[] | null;
  classTeamMatchupScoring?: TeamMatchupScoring | null;
  tournamentTeamMatchupScoring?: TeamMatchupScoring | null;
  tourTeamMatchupScoring?: TeamMatchupScoring | null;
  organizationTeamMatchupScoring?: TeamMatchupScoring | null;
  /**
   * When false, strip team-only criteria from ranking `effective`.
   * When true/undefined, keep them (undefined = parent-scope display).
   */
  isTeamsClass?: boolean;
  rulesVersion?: number | null;
}): GroupStandingsTieBreakerResolution => {
  const normalize = (
    tieBreakers?: GroupStandingsTieBreakerEnum[] | null
  ): GroupStandingsTieBreakerEnum[] | null => {
    if (!tieBreakers?.length) {
      return null;
    }

    const allowed = new Set(Object.values(GroupStandingsTieBreakerEnum));
    const normalized = tieBreakers.filter((item, index, arr) => {
      return allowed.has(item) && arr.indexOf(item) === index;
    });

    return normalized.length ? normalized : null;
  };

  const defaults = defaultGroupStandingsTieBreakersForClass({
    isTeamsClass: scope.isTeamsClass,
    rulesVersion: scope.rulesVersion,
  });

  const chain: GroupStandingsTieBreakerResolution['chain'] = {
    class: normalize(scope.classTieBreakers),
    tournament: normalize(scope.tournamentTieBreakers),
    tour: normalize(scope.tourTieBreakers),
    organization: normalize(scope.organizationTieBreakers),
    default: defaults,
  };

  let source: GroupStandingsTieBreakerSource;
  let effective: GroupStandingsTieBreakerEnum[];

  if (chain.class) {
    source = 'class';
    effective = chain.class;
  } else if (chain.tournament) {
    source = 'tournament';
    effective = chain.tournament;
  } else if (chain.tour) {
    source = 'tour';
    effective = chain.tour;
  } else if (chain.organization) {
    source = 'organization';
    effective = chain.organization;
  } else {
    source = 'default';
    effective = chain.default;
  }

  if (scope.isTeamsClass === false) {
    effective = stripTeamOnlyGroupStandingsTieBreakers(effective);
  }

  const scoring = resolveScoringFromChain(scope);

  return { effective, source, chain, ...scoring };
};
